import { Button, Group, Paper, Stack, Text, Title } from '@mantine/core';
import { IconCar, IconLock, IconTool, type Icon } from '@tabler/icons-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { useDelavnica, useProdaja } from '../seja/paket';
import { NAZIVI_PODROCIJ, usePravica, type Podrocje, type Raven } from '../seja/pravice';
import { useSeja } from '../seja/useSeja';
import { barve } from '../tema';

/**
 * Zaslon, za katerega je potrebna pravica (vloga uporabnika): brez nje namesto zaslona pojasnilo, katere pravice
 * manjka, s povezavo na navodila (strežnik take zahteve zavrne, NI_PRAVICE).
 */
export function SamoPravica({
  podrocje,
  raven = 'PREGLED',
  children,
}: {
  podrocje: Podrocje;
  raven?: Raven;
  children: ReactNode;
}) {
  const stanje = useSeja();
  if (usePravica(podrocje, raven)) {
    return children;
  }
  const vloga = stanje.status === 'prijavljen' ? stanje.uporabnik.vloga : undefined;
  const kdo = stanje.status === 'prijavljen' ? (stanje.urejajoPravice ?? []) : [];
  return (
    <Stack align="center" mt={60}>
      <Paper radius="lg" p={40} shadow="xs" maw={520} ta="center">
        <IconLock size={40} color={barve.primarna} stroke={1.6} />
        <Title order={2} size="h3" mt="md">
          Za to nimate pravice
        </Title>
        <Text c="dimmed" mt="xs">
          Za ta zaslon potrebujete pravico »{NAZIVI_PODROCIJ[podrocje]}« (
          {raven === 'UREJANJE' ? 'urejanje' : 'pregled'}){vloga ? `, vaša vloga »${vloga}« je nima` : ''}.{' '}
          {kdo.length > 0 ? `Pravice vam lahko dodeli: ${kdo.join(', ')}.` : 'Pravice ureja skrbnik podjetja.'}
        </Text>
        <Group justify="center" mt="lg">
          <Button component={Link} to="/pomoc/vloge-in-pravice" variant="light">
            Navodila
          </Button>
          <Button component={Link} to="/">
            Začetni zaslon
          </Button>
        </Group>
      </Paper>
    </Stack>
  );
}

/**
 * Zaslon modula Prodaja vozil (vozila, povpraševanja, analize, DMV): brez modula v paketu namesto njega pojasnilo s
 * povezavo na Naročnino, kjer se modul doda (strežnik te funkcije zavrne, MODUL_NI_VKLJUCEN).
 */
export function SamoProdaja({ children }: { children: ReactNode }) {
  return useProdaja() ? children : <BrezModula ime="Prodaja vozil" Ikona={IconCar} />;
}

/** Zaslon modula Delavnica (delovni nalogi, vozila strank): brez modula v paketu pojasnilo kot {@link SamoProdaja}. */
export function SamoDelavnica({ children }: { children: ReactNode }) {
  return useDelavnica() ? children : <BrezModula ime="Delavnica" Ikona={IconTool} />;
}

function BrezModula({ ime, Ikona }: { ime: string; Ikona: Icon }) {
  return (
    <Stack align="center" mt={60}>
      <Paper radius="lg" p={40} shadow="xs" maw={520} ta="center">
        <Ikona size={40} color={barve.primarna} stroke={1.6} />
        <Title order={2} size="h3" mt="md">
          Modul {ime}
        </Title>
        <Text c="dimmed" mt="xs">
          Ta funkcija je v modulu {ime}, ki ga vaš paket ne vključuje. Dodate ga na zaslonu Naročnina.
        </Text>
        <Button component={Link} to="/narocnina" mt="lg">
          Naročnina
        </Button>
      </Paper>
    </Stack>
  );
}
